import {
  BoxProps,
  Button,
  ButtonProps,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverProps,
  PopoverTrigger,
  Portal,
  useDisclosure,
} from "@chakra-ui/react";
import { forwardRef, ReactElement, ReactNode, useRef } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import {
  Z_INDEX_PORTAL_ESCAPE,
  Z_INDEX_POPOVER,
} from "../../screens/root/constants";

export interface DropdownOption {
  id: string;
  label: ReactNode;
  action: () => void;
  disabled?: boolean;
  ctaText?: string;
  ctaVariant?:
    | "primary"
    | "secondary"
    | "contextualCTASecondary"
    | "contextualCTAPrimary";
  testingSelector?: string;
}

export interface ContextualCTAButtonProps
  extends Omit<ButtonProps, "variant" | "size" | "isDisabled" | "onClick"> {
  children: ReactNode;
  variant?: "primary" | "secondary";
  size?: "sm" | "md" | "lg";
  dropdownOptions?: DropdownOption[];
  dropdownContent?: ReactNode;
  isDisabled?: boolean;
  onClick?: () => void;
  expandIcon?: ReactElement;
  collapseIcon?: ReactElement;
  placement?: PopoverProps["placement"];
  popoverMinWidth?: BoxProps["minWidth"];
  escapeContainerBounds?: boolean; // Use Portal rendering to escape container overflow constraints
  enableFlipAndPreventOverflow?: boolean; // Enable automatic flipping and overflow prevention for viewport boundaries
}

export const ContextualCTAButton = forwardRef<
  HTMLButtonElement,
  ContextualCTAButtonProps
>(
  (
    {
      children,
      variant = "primary",
      size = "md",
      dropdownOptions = [],
      dropdownContent,
      isDisabled = false,
      onClick,
      expandIcon = <ChevronDownIcon boxSize="16px" />,
      collapseIcon = <ChevronUpIcon boxSize="16px" />,
      placement = "bottom",
      popoverMinWidth = "280px",
      escapeContainerBounds = false,
      enableFlipAndPreventOverflow = false,
      fontSize,
      borderRadius,
      ...buttonProps
    },
    ref,
  ) => {
    const { isOpen, onToggle, onClose } = useDisclosure();
    const hasDropdown = dropdownOptions.length > 0 || dropdownContent;
    const popoverRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);

    let adaptiveFontSize = "13px";
    let adaptiveBorderRadius = "12px";
    let iconHorizontalPadding = "10px";
    switch (size) {
      case "sm":
        adaptiveFontSize = "12px";
        adaptiveBorderRadius = "10px";
        iconHorizontalPadding = "8px";
        break;
      case "md":
        adaptiveFontSize = "13px";
        adaptiveBorderRadius = "12px";
        iconHorizontalPadding = "10px";
        break;
      case "lg":
        adaptiveFontSize = "16px";
        adaptiveBorderRadius = "20px";
        iconHorizontalPadding = "12px";
        break;
    }

    fontSize = fontSize || adaptiveFontSize;
    borderRadius = borderRadius || adaptiveBorderRadius;

    const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (hasDropdown) {
        onToggle();
      } else if (onClick) {
        onClick();
      }
    };

    // If no dropdown, render as regular button
    if (!hasDropdown) {
      return (
        <Button
          ref={ref}
          variant={variant}
          size={size}
          isDisabled={isDisabled}
          onClick={onClick}
          fontSize={fontSize}
          borderRadius={borderRadius}
          {...buttonProps}
        >
          {children}
        </Button>
      );
    }

    const borderGradient = `1px solid linear-gradient(180deg, rgba(255, 255, 255, 0.20) 22.5%, rgba(255, 255, 255, 0.00) 90%)`;

    // Compare popover width to button width for alignment
    const getPopoverPlacement = (): PopoverProps["placement"] => {
      if (placement !== "bottom" && placement !== "top") return placement;

      const buttonMaxWidth = buttonProps.maxWidth;
      const actualButtonWidth = triggerRef.current?.offsetWidth;

      const popoverMinWidthNum = parseFloat(String(popoverMinWidth));

      // Use actual button width if available, otherwise try to parse maxWidth
      let buttonWidthToCompare: number | null = null;

      if (actualButtonWidth) {
        buttonWidthToCompare = actualButtonWidth;
      } else if (buttonMaxWidth) {
        const buttonMaxWidthNum = parseFloat(String(buttonMaxWidth));
        if (!isNaN(buttonMaxWidthNum)) {
          buttonWidthToCompare = buttonMaxWidthNum;
        }
      }

      const useStart =
        !buttonWidthToCompare || popoverMinWidthNum > buttonWidthToCompare;
      const suffix = useStart ? "start" : "end";

      return `${placement}-${suffix}` as PopoverProps["placement"];
    };

    return (
      <Popover
        isOpen={isOpen}
        onClose={onClose}
        placement={getPopoverPlacement()}
        closeOnBlur={true}
        flip={enableFlipAndPreventOverflow}
        boundary="clippingParents"
        preventOverflow={enableFlipAndPreventOverflow}
        offset={[0, 8]}
      >
        <PopoverTrigger>
          <Button
            ref={(node) => {
              if (typeof ref === "function") ref(node);
              else if (ref)
                (
                  ref as React.MutableRefObject<HTMLButtonElement | null>
                ).current = node;
              (
                triggerRef as React.MutableRefObject<HTMLButtonElement | null>
              ).current = node;
            }}
            variant={variant}
            size={size}
            onClick={handleButtonClick}
            isDisabled={isDisabled}
            padding="0 0 0 16px"
            fontSize={fontSize}
            borderRadius={borderRadius}
            {...buttonProps}
          >
            <Flex
              flexDirection="row"
              alignItems="center"
              width="100%"
              position="relative"
              gap="8px"
              height="100%"
            >
              <Flex
                alignItems="center"
                justifyContent="center"
                width="100%"
                flex="1"
                textAlign="center"
              >
                {children}
              </Flex>
              <Flex
                borderLeft={borderGradient}
                borderTop={borderGradient}
                borderRight={borderGradient}
                borderTopRightRadius={borderRadius}
                borderBottomRightRadius={borderRadius}
                alignItems="center"
                background="linear-gradient(180deg, #99361A 0%, var(--Blaze-Blaze-600, #CC4823) 100%)"
                height="100%"
                padding={`0 ${iconHorizontalPadding}`}
              >
                {isOpen ? collapseIcon : expandIcon}
              </Flex>
            </Flex>
          </Button>
        </PopoverTrigger>

        {escapeContainerBounds ? (
          <Portal containerRef={undefined}>
            <div
              style={{
                display: isOpen ? "block" : "none",
                position: "relative",
                zIndex: Z_INDEX_PORTAL_ESCAPE,
              }}
            >
              <PopoverContentComponent
                popoverRef={popoverRef}
                popoverMinWidth={popoverMinWidth}
                escapeContainerBounds={escapeContainerBounds}
                dropdownContent={dropdownContent}
                dropdownOptions={dropdownOptions}
                onClose={onClose}
              />
            </div>
          </Portal>
        ) : (
          <PopoverContentComponent
            popoverRef={popoverRef}
            popoverMinWidth={popoverMinWidth}
            escapeContainerBounds={escapeContainerBounds}
            dropdownContent={dropdownContent}
            dropdownOptions={dropdownOptions}
            onClose={onClose}
          />
        )}
      </Popover>
    );
  },
);

interface PopoverContentComponentProps {
  popoverRef: React.RefObject<HTMLDivElement>;
  popoverMinWidth: BoxProps["minWidth"];
  escapeContainerBounds: boolean;
  dropdownContent?: ReactNode;
  dropdownOptions: DropdownOption[];
  onClose: () => void;
}

const PopoverContentComponent = ({
  popoverRef,
  popoverMinWidth,
  dropdownContent,
  dropdownOptions,
  onClose,
}: PopoverContentComponentProps) => (
  <PopoverContent
    ref={popoverRef}
    w="fit-content"
    minW={popoverMinWidth}
    bg="transparent.black.90"
    border="1px solid"
    borderColor="transparent.white.10"
    borderRadius="16px"
    boxShadow="rgba(0, 0, 0, 0.4)"
    backdropFilter="blur(18px)"
    zIndex={Z_INDEX_POPOVER}
  >
    <PopoverBody p={2}>
      {dropdownContent ? (
        dropdownContent
      ) : (
        <Flex direction="column" id="popoverContentId" gap={1}>
          {dropdownOptions.map((option) => (
            <Flex
              key={option.id}
              alignItems="center"
              justifyContent="space-between"
              p={3}
              borderRadius="16px"
              _hover={{
                bg: "transparent.white.10",
                color: "dune.100",
              }}
              cursor={option.disabled ? "not-allowed" : "pointer"}
              opacity={option.disabled ? 0.5 : 1}
              transition="all 0.2s"
              fontWeight="400"
              fontSize="14px"
              letterSpacing="0.5"
              lineHeight="130%"
              color="dune.100"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                option.action();
                onClose();
              }}
            >
              <Flex alignItems="center" flex="1">
                {option.label}
              </Flex>
              {option.ctaText && (
                <Button
                  size="sm"
                  variant={option.ctaVariant}
                  isDisabled={option.disabled}
                  height="26px"
                  borderRadius="9px"
                  gc-testing-id={option.testingSelector}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    option.action();
                    onClose();
                  }}
                  ml="32px"
                  minW="68px"
                >
                  {option.ctaText}
                </Button>
              )}
            </Flex>
          ))}
        </Flex>
      )}
    </PopoverBody>
  </PopoverContent>
);
